import {
    addDoc,
    collection,
    deleteDoc,
    doc,
    getDoc,
    getDocs,
    query,
    serverTimestamp,
    setDoc,
    where,
} from 'firebase/firestore';
import { deleteObject, getDownloadURL, getStorage, ref, uploadBytesResumable } from 'firebase/storage';
import { db } from '@/config/firebase';
import {
    RECEIPT_ACCEPTED_TYPE,
    buildReceiptStoragePath,
    validateReceiptFile,
} from '../utils/vacationReceiptFiles';
import type { VacationReceipt, VacationReceiptUpload } from '../models/vacationReceipt.types';

const COLLECTION_NAME = 'vacationReceipts';

export const RECEIPT_INVALID_COLLABORATOR_CODE = 'vacations/colaborador-invalido';

export const RECEIPT_INVALID_COLLABORATOR_MESSAGE =
    `${RECEIPT_INVALID_COLLABORATOR_CODE}: O cadastro do colaborador precisa ter e-mail para o ` +
    'recibo poder ser lido por ele depois.';

export const RECEIPT_READ_DENIED_MESSAGE =
    'Suas permissões não alcançam os recibos deste colaborador.';

export const RECEIPT_WRITE_DENIED_MESSAGE =
    'O banco recusou a gravação do recibo. Confirme se as regras de vacationReceipts foram publicadas.';

export const RECEIPT_DUPLICATE_MESSAGE =
    'Este período já tem um recibo anexado. Para corrigir, exclua o anterior e anexe o novo.';

export interface CreateReceiptInput extends VacationReceiptUpload {
    collaboratorId: string;
    collaboratorEmail: string;
    uploadedBy: string;
    onProgress?: (percent: number) => void;
}

function buildReceiptDocId(requestId: string | null, segmentOrder: number | null): string | null {
    if (!requestId) return null;
    return `${requestId}_${segmentOrder === null ? 'all' : String(segmentOrder)}`;
}

function errorCode(error: unknown): string | undefined {
    if (typeof error !== 'object' || error === null) return undefined;
    return (error as { code?: string }).code;
}

function isPermissionDenied(error: unknown): boolean {
    return errorCode(error) === 'permission-denied';
}

function isObjectMissing(error: unknown): boolean {
    return errorCode(error) === 'storage/object-not-found';
}

class VacationReceiptServiceClass {
    private collectionName = COLLECTION_NAME;

    async listByCollaborator(collaboratorId: string, ownerEmail?: string): Promise<VacationReceipt[]> {
        if (!collaboratorId) return [];

        const constraint = ownerEmail
            ? where('collaboratorEmail', '==', ownerEmail.trim().toLowerCase())
            : where('collaboratorId', '==', collaboratorId);

        const snapshot = await getDocs(query(collection(db, this.collectionName), constraint));

        return snapshot.docs
            .map((snap) => this.formatReceipt(snap.id, snap.data()))
            .sort((a, b) => {
                const left = a.uploadedAt?.toMillis?.() ?? 0;
                const right = b.uploadedAt?.toMillis?.() ?? 0;
                return right - left;
            });
    }

    async create(input: CreateReceiptInput): Promise<string> {
        if (!input.collaboratorId || !input.collaboratorEmail?.trim()) {
            throw new Error(RECEIPT_INVALID_COLLABORATOR_MESSAGE);
        }

        const fileError = validateReceiptFile(input.file);
        if (fileError) throw new Error(fileError);

        const storagePath = buildReceiptStoragePath(
            input.collaboratorId,
            input.file.name,
            Date.now()
        );

        await this.uploadToStorage(storagePath, input.file, input.onProgress);

        const docId = buildReceiptDocId(input.requestId || null, input.segmentOrder ?? null);

        try {
            if (docId) {
                const existingDoc = await getDoc(doc(db, this.collectionName, docId));
                if (existingDoc.exists()) {
                    throw new Error(RECEIPT_DUPLICATE_MESSAGE);
                }

                await setDoc(doc(db, this.collectionName, docId), {
                    collaboratorId: input.collaboratorId,
                    collaboratorEmail: input.collaboratorEmail.trim().toLowerCase(),
                    requestId: input.requestId || null,
                    segmentOrder: input.segmentOrder ?? null,
                    referenceStartDate: input.referenceStartDate || null,
                    referenceEndDate: input.referenceEndDate || null,
                    fileName: input.file.name,
                    storagePath,
                    contentType: input.file.type || RECEIPT_ACCEPTED_TYPE,
                    sizeBytes: input.file.size,
                    source: 'manual',
                    signedAt: null,
                    note: input.note || null,
                    uploadedBy: input.uploadedBy,
                    uploadedAt: serverTimestamp(),
                });

                return docId;
            } else {
                const docRef = await addDoc(collection(db, this.collectionName), {
                    collaboratorId: input.collaboratorId,
                    collaboratorEmail: input.collaboratorEmail.trim().toLowerCase(),
                    requestId: null,
                    segmentOrder: null,
                    referenceStartDate: input.referenceStartDate || null,
                    referenceEndDate: input.referenceEndDate || null,
                    fileName: input.file.name,
                    storagePath,
                    contentType: input.file.type || RECEIPT_ACCEPTED_TYPE,
                    sizeBytes: input.file.size,
                    source: 'manual',
                    signedAt: null,
                    note: input.note || null,
                    uploadedBy: input.uploadedBy,
                    uploadedAt: serverTimestamp(),
                });

                return docRef.id;
            }
        } catch (error) {
            try {
                await this.deleteObject(storagePath);
            } catch (rollbackError) {
                console.warn(
                    `[vacations] arquivo de recibo não removido: ${storagePath}`,
                    rollbackError
                );
            }

            if (isPermissionDenied(error)) throw new Error(RECEIPT_WRITE_DENIED_MESSAGE);
            if (error instanceof Error && error.message === RECEIPT_DUPLICATE_MESSAGE) {
                throw error;
            }
            throw error;
        }
    }

    async openFile(receipt: Pick<VacationReceipt, 'storagePath' | 'fileUrl'>): Promise<void> {
        const popup = window.open('about:blank', '_blank');
        try {
            const url = receipt.storagePath
                ? await getDownloadURL(ref(getStorage(), receipt.storagePath))
                : receipt.fileUrl;
            if (popup) {
                popup.opener = null;
                popup.location.href = url;
            } else {
                window.location.href = url;
            }
        } catch (error) {
            popup?.close();
            throw error;
        }
    }

    async remove(receipt: Pick<VacationReceipt, 'id' | 'storagePath'>): Promise<void> {
        await deleteDoc(doc(db, this.collectionName, receipt.id));
        if (receipt.storagePath) await this.deleteObject(receipt.storagePath);
    }

    private async deleteObject(path: string): Promise<void> {
        try {
            await deleteObject(ref(getStorage(), path));
        } catch (error) {
            if (!isObjectMissing(error)) throw error;
        }
    }

    private uploadToStorage(
        path: string,
        file: File,
        onProgress?: (percent: number) => void
    ): Promise<void> {
        const storageRef = ref(getStorage(), path);
        const task = uploadBytesResumable(storageRef, file, {
            contentType: RECEIPT_ACCEPTED_TYPE,
        });

        return new Promise((resolve, reject) => {
            task.on(
                'state_changed',
                (snapshot) =>
                    onProgress?.((snapshot.bytesTransferred / snapshot.totalBytes) * 100),
                (error) => reject(error),
                () => resolve()
            );
        });
    }


    private formatReceipt(id: string, data: Record<string, unknown>): VacationReceipt {
        return {
            id,
            collaboratorId: String(data.collaboratorId ?? ''),
            collaboratorEmail: String(data.collaboratorEmail ?? ''),
            requestId: (data.requestId ?? null) as string | null,
            segmentOrder: (data.segmentOrder ?? null) as number | null,
            referenceStartDate: (data.referenceStartDate ?? null) as string | null,
            referenceEndDate: (data.referenceEndDate ?? null) as string | null,
            fileName: String(data.fileName ?? 'recibo.pdf'),
            fileUrl: String(data.fileUrl ?? ''),
            storagePath: String(data.storagePath ?? ''),
            contentType: String(data.contentType ?? RECEIPT_ACCEPTED_TYPE),
            sizeBytes: Number(data.sizeBytes ?? 0),
            source: data.source === 'd4sign' ? 'd4sign' : 'manual',
            signedAt: (data.signedAt ?? null) as VacationReceipt['signedAt'],
            note: (data.note ?? null) as string | null,
            uploadedBy: String(data.uploadedBy ?? ''),
            uploadedAt: (data.uploadedAt ?? null) as VacationReceipt['uploadedAt'],
        };
    }
}

export const vacationReceiptService = new VacationReceiptServiceClass();
